import type { LumoraClient, TenantLogEntry } from "@lumora/bridge";
import { errorMessage } from "@lumora/bridge";
import { useCallback, useEffect, useState } from "react";
import { appendEntries, LOGS_PAGE_SIZE, logsRow, type LogsKindFilter } from "./logsView.js";

export interface LogsPanelProps {
  entries: TenantLogEntry[];
  kind: LogsKindFilter;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onKind: (k: LogsKindFilter) => void;
  onMore: () => void;
}

const FILTERS: { key: LogsKindFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "request", label: "Requests" },
  { key: "event", label: "Events" },
];

/** Presentational logs table — no data fetching. */
export function LogsPanel({ entries, kind, loading, error, hasMore, onKind, onMore }: LogsPanelProps) {
  return (
    <section className="portal-section" data-lumora-view="logs">
      <h2>Logs</h2>
      <div role="group" aria-label="Filter logs by kind" className="portal-logs-filter">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={kind === f.key}
            disabled={loading}
            onClick={() => onKind(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error ? (
        <p className="portal-error" data-lumora-error>
          {error}
        </p>
      ) : null}
      {loading && entries.length === 0 ? <p>Loading…</p> : null}
      {!loading && !error && entries.length === 0 ? (
        <p className="muted" data-lumora-logs-empty>
          No log entries yet.
        </p>
      ) : null}

      {entries.length > 0 ? (
        <div className="portal-table-scroll" style={{ overflowX: "auto" }}>
          <table className="portal-table" data-lumora-logs-table>
            <thead>
              <tr>
                <th>When</th>
                <th>Kind</th>
                <th>Method</th>
                <th>Path</th>
                <th>Status</th>
                <th>Actor / Message</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const r = logsRow(e);
                return (
                  <tr key={r.id}>
                    <td>{r.when}</td>
                    <td>{r.kind}</td>
                    <td>{r.method}</td>
                    <td>{r.path}</td>
                    <td>{r.status}</td>
                    <td>{r.detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {hasMore ? (
        <button type="button" disabled={loading} onClick={onMore}>
          {loading ? "Loading…" : "Load more"}
        </button>
      ) : null}
    </section>
  );
}

export interface LogsSectionProps {
  client: Pick<LumoraClient, "getTenantLogs">;
}

/** Owner Logs section (GET /v1/logs). Render only for users with logs:read. */
export function LogsSection({ client }: LogsSectionProps) {
  const [kind, setKind] = useState<LogsKindFilter>("all");
  const [entries, setEntries] = useState<TenantLogEntry[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (k: LogsKindFilter, before: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const page = await client.getTenantLogs({
          limit: LOGS_PAGE_SIZE,
          ...(before ? { before } : {}),
          ...(k !== "all" ? { kind: k } : {}),
        });
        setEntries((prev) => (before ? appendEntries(prev, page.entries) : page.entries));
        setNext(page.next);
      } catch (err) {
        setError(errorMessage(err, "Could not load logs."));
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  useEffect(() => {
    void load(kind, null);
  }, [load, kind]);

  return (
    <LogsPanel
      entries={entries}
      kind={kind}
      loading={loading}
      error={error}
      hasMore={next !== null}
      onKind={(k) => {
        if (k === kind) return;
        setEntries([]);
        setNext(null);
        setKind(k);
      }}
      onMore={() => {
        if (next) void load(kind, next);
      }}
    />
  );
}
